"use client";

import * as React from "react";
import Script from "next/script";
import { usePathname } from "next/navigation";

import {
  ADS_ENABLED,
  GAM_ENV,
  GAM_PILOT,
  countFor,
  deviceForWidth,
  FORMATS,
  sizesFor,
  toGptSize,
  unitCode,
  unitPath,
  type Brand,
  type Device,
  type DisplayFormat,
  type SizeSet,
  type SizeToken,
  nextIndex,
} from "@/lib/ads/config";
import { GPT_SRC, gpt, type GptRenderEndedEvent, type GptSlot } from "@/lib/ads/gpt";
import { installPublicApi } from "./public-api";
import {
  buildTargeting,
  deriveFromPath,
  readAdTest,
  readChannel,
  readTier,
  readUserType,
  type PageTargeting,
  type PageType,
} from "@/lib/ads/targeting";

/**
 * One ad runtime for the whole site.
 *
 * - Loads gpt.js once (afterInteractive) and configures pubads: SRA, lazy load,
 *   page-level key-values (no collapse-empty: slots keep their reserved height).
 * - Derives brand / section / page from the pathname; pages may override via
 *   <PageAdMeta>. Recomputed on every App-Router navigation: old slots are
 *   destroyed, targeting is cleared and re-set, new slots define + display.
 * - Numbers auto-indexed units (`unit="LargeRectangle"`) in DOM order, so the
 *   first LREC in a rail is LargeRectangle1 without every call site knowing its
 *   position. Explicit numbers (`LargeRectangle2`) always win.
 * - Works with no network code: nothing is requested, but every slot still gets
 *   its `data-ad-*` attributes so the QA harness can assert the inventory.
 */

export interface SlotRegistration {
  el: HTMLElement;
  format: DisplayFormat;
  index?: number;
  sizes: SizeSet;
  outOfPage: boolean;
  /** The page places the creative itself (skin wallpaper, light box): every band size is requested as is. */
  overlay?: boolean;
  /** Growing box: the slot may take a SafeFrame push expansion (billboard / pushdown on the top band). */
  grow?: number | true;
  /** Absolute path of an EXISTING unit on the network (pilot mode); bypasses the taxonomy path. */
  legacyPath?: string;
  /** Called with the resolved unit code (e.g. HKEJ_Web_LargeRectangle2). */
  onResolve?: (code: string, path: string) => void;
  onRender?: (e: GptRenderEndedEvent) => void;
}

interface Registered extends SlotRegistration {
  id: string;
  code?: string;
  path?: string;
  slot?: GptSlot | null;
  device?: Device;
  /** Torn down (unregistered) while a define command was still queued. */
  dead?: boolean;
  /** This native slot already came back with a creative (its path can be reused). */
  servedPath?: string;
  /** Empty native slot waiting for a sibling that did serve, so it can reuse that unit. */
  pendingRetry?: boolean;
  /** Already redefined once onto a unit that served. A second empty answer stays empty. */
  retried?: boolean;
}

interface AdRuntime {
  enabled: boolean;
  /** Pilot mode: only slots with a `legacyPath` request ads. */
  pilot: boolean;
  /** gpt.js failed to load (blocked / offline): every live slot is treated as empty. */
  gptFailed: boolean;
  /** Draw every slot as a labelled box at its real size (design review / screenshots). */
  preview: boolean;
  brand: Brand;
  targeting: PageTargeting;
  device: Device;
  register(reg: SlotRegistration): () => void;
  setMeta(meta: PageMeta | null): void;
}

export interface PageMeta {
  page?: PageType;
  section?: string[];
  keyword?: string[];
  stockcode?: string;
}

const Ctx = React.createContext<AdRuntime | null>(null);

export function useAdRuntime(): AdRuntime {
  const v = React.useContext(Ctx);
  if (!v) {
    // Slots outside the provider (storybook, isolated renders) behave as disabled.
    return {
      enabled: false,
      pilot: false,
      gptFailed: false,
      preview: false,
      brand: "HKEJ",
      targeting: { brand: "HKEJ", section: [], page: "other", adsAllowed: false },
      device: "desktop",
      register: () => () => {},
      setMeta: () => {},
    };
  }
  return v;
}

let seq = 0;

export function AdProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname() ?? "/";
  const [meta, setMetaState] = React.useState<PageMeta | null>(null);
  const [device, setDevice] = React.useState<Device>("desktop");
  const [preview, setPreview] = React.useState(false);
  const [gptFailed, setGptFailed] = React.useState(false);
  /* window.hkejAds + the legacy dismiss_skinner_Ad() — for creatives and the console; also with ads off. */
  React.useEffect(() => { installPublicApi(); }, []);
  /* Ad-blocker detection: gpt.js errors out (onError below) or never becomes ready.
     Either way every live band disappears — no 廣告 label, no reserved box. */
  React.useEffect(() => {
    if (!ADS_ENABLED) return;
    const t = window.setTimeout(() => {
      if (!window.googletag?.apiReady) setGptFailed(true);
    }, 8000);
    return () => window.clearTimeout(t);
  }, []);
  React.useEffect(() => {
    try {
      const q = new URLSearchParams(window.location.search);
      if (q.has("adpreview")) window.sessionStorage.setItem("ej_adpreview", q.get("adpreview") === "0" ? "" : "1");
      setPreview(process.env.NEXT_PUBLIC_AD_PREVIEW === "1" || window.sessionStorage.getItem("ej_adpreview") === "1");
    } catch { /* storage blocked */ }
  }, [pathname]);
  const regs = React.useRef<Map<string, Registered>>(new Map());
  const syncScheduled = React.useRef(false);
  const initialised = React.useRef(false);

  const base = React.useMemo(() => deriveFromPath(pathname), [pathname]);
  const targeting: PageTargeting = React.useMemo(
    () => ({
      brand: base.brand,
      section: meta?.section ?? base.section,
      page: meta?.page ?? base.page,
      keyword: meta?.keyword,
      stockcode: meta?.stockcode,
      adsAllowed: base.adsAllowed,
    }),
    [base, meta],
  );

  /* viewport band */
  React.useEffect(() => {
    const update = () => setDevice(deviceForWidth(window.innerWidth));
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  /* one-time pubads configuration */
  React.useEffect(() => {
    if (!ADS_ENABLED || initialised.current) return;
    initialised.current = true;
    gpt((g) => {
      const p = g.pubads();
      p.enableSingleRequest();
      p.enableLazyLoad({ fetchMarginPercent: 100, renderMarginPercent: 50, mobileScaling: 2 });
      // No collapseEmptyDivs: GPT would `display:none` every slot until an ad renders,
      // which defeats the reserved heights (page jumps) and hid the native fallbacks.
      // Visibility is driven by data-ad-state + the scoped styles instead.
      p.setCentering(true);
      // Expandable creatives (rate card: expandable SBB / LREC; pushdown): overlay expansion is
      // allowed everywhere (it never moves the page), push expansion only on slots that opted in
      // with `grow` (see the define loop) — a mis-trafficked pushdown on a rail slot cannot move
      // the layout. setForceSafeFrame stays off: the skin / light-box 1x1 customs are friendly iframes.
      p.setSafeFrameConfig({ allowOverlayExpansion: true, allowPushExpansion: false });
      p.setPrivacySettings({ restrictDataProcessing: false });
      p.addEventListener("slotRenderEnded", (e) => {
        const id = e.slot.getSlotElementId();
        const r = regs.current.get(id);
        if (!r || r.dead) return;
        const native = Boolean(FORMATS[r.format]?.native);
        // The phone cube is one sponsorship creative, and it serves once per page.
        // The other large rectangles in that same request come back empty. Asking
        // again lets the house 300×250 fill them. The box stays in its pending
        // height until that answer, so it does not collapse and pop back. One look.
        if (!native && e.isEmpty && r.format === "LargeRectangle" && !r.retried && r.el.isConnected) {
          r.retried = true;
          p.refresh([e.slot]);
          return;
        }
        if (native && !e.isEmpty && r.path) r.servedPath = r.path;
        r.onRender?.(e);
        if (!native) return;
        const donors = () => [...regs.current.values()].filter((x) => x.format === r.format && x.servedPath && x.el.querySelector("iframe"));
        // GAM will not send the same native creative to a second slot on this page.
        // A later every-3rd row copies a creative that already painted, so the
        // space stays filled. Copying fails closed: the editorial row stays.
        const mirror = (target: Registered, donor: Registered) => {
          if (target.retried || target.dead || !target.el.isConnected || donor.el === target.el) return false;
          const host = donor.el.querySelector<HTMLElement>('div[id^="google_ads_iframe_"]');
          const frame = host?.querySelector("iframe") ?? donor.el.querySelector<HTMLIFrameElement>("iframe");
          if (!frame) return false;
          const dh = Math.round(donor.el.getBoundingClientRect().height);
          const src = frame.getAttribute("src") || "";
          // A clone with no address, or a donor that has not painted, would
          // replace the article with a blank row.
          if (!src || dh < 20) return false;
          target.retried = true;
          target.pendingRetry = false;
          const copy = (host ?? frame).cloneNode(true) as HTMLElement;
          copy.id = `google_ads_iframe_mirror_${target.id}`;
          copy.querySelectorAll("[id]").forEach((n) => n.removeAttribute("id"));
          const copiedFrame = copy.matches("iframe") ? copy : copy.querySelector("iframe");
          if (copiedFrame) {
            copiedFrame.setAttribute("height", String(dh));
            copiedFrame.style.width = "100%";
            copiedFrame.style.height = `${dh}px`;
          }
          copy.style.width = "100%";
          copy.style.height = `${dh}px`;
          target.el.style.height = `${dh}px`;
          target.el.replaceChildren(copy);
          target.el.setAttribute("data-ad-state", "fluid");
          target.el.parentElement?.setAttribute("data-ad-wrap-state", "fluid");
          const slot = donor.slot ?? target.slot;
          if (slot) target.onRender?.({ isEmpty: false, size: "fluid", slot });
          return true;
        };
        const flush = () => {
          const sold = donors();
          if (!sold.length) return;
          let n = 0;
          for (const w of regs.current.values()) {
            if (!w.pendingRetry || w.retried || w.format !== r.format) continue;
            const donor = sold[n % sold.length];
            if (mirror(w, donor)) n += 1;
          }
        };
        if (!e.isEmpty) { flush(); return; }
        // A trailing native with no article behind it stays empty when GAM
        // sends nothing. Copying a sibling creative would invent a card.
        // A repeated unit (row 12 is InBanner1 again, row 15 is InBanner2, …)
        // shows its article when GAM has no further creative. Copying a sibling
        // would hide that article.
        if (r.retried || r.el.hasAttribute("data-ad-keep-empty") || r.el.hasAttribute("data-ad-request-overflow")) return;
        const sold = donors();
        if (!sold.length) { r.pendingRetry = true; return; }
        r.pendingRetry = true;
        flush();
      });
      g.enableServices();
    });
  }, []);

  /* page-level targeting: reset on every navigation / meta change */
  React.useEffect(() => {
    if (!ADS_ENABLED) return;
    const kv = buildTargeting(targeting, readUserType(), readChannel(), GAM_ENV || undefined, readTier(), readAdTest());
    gpt((g) => {
      const p = g.pubads();
      p.clearTargeting();
      for (const [k, v] of Object.entries(kv)) p.setTargeting(k, v);
    });
  }, [targeting]);

  /* Destroy every slot when the route changes; the new page's slots re-register.
     Out-of-page creatives (skin wallpaper, gutter links) attach themselves to <body>
     outside React and mark every node with `data-skin`; they go with the page too. */
  React.useEffect(() => {
    return () => {
      if (!ADS_ENABLED) return;
      const live = [...regs.current.values()].map((r) => r.slot).filter(Boolean) as GptSlot[];
      if (live.length) gpt((g) => g.destroySlots(live));
      for (const r of regs.current.values()) r.slot = undefined;
      // React owns the skin host ([data-ad-host]); it clears its own attribute.
      document.querySelectorAll("[data-skin]:not([data-ad-host])").forEach((n) => n.remove());
    };
  }, [pathname]);

  const brand = targeting.brand;

  /** Assign DOM-order numbers, define new slots, display them. Batched per frame. */
  const sync = React.useCallback(() => {
    syncScheduled.current = false;
    const all = [...regs.current.values()].filter((r) => r.el.isConnected);
    all.sort((a, b) => (a.el.compareDocumentPosition(b.el) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));

    const band = deviceForWidth(window.innerWidth);
    // A unit is on this page only when its container is actually rendered and its
    // size map has sizes for this band. A `display:none` gate (the phone LREC at
    // desktop, `hidden lg:flex` half pages) must not take a number: numbering it
    // would shift the rail's first slot.
    const rendered = (r: Registered) => r.outOfPage || r.el.getClientRects().length > 0;
    const inPlay = (r: Registered) => rendered(r) && r.sizes[band].length > 0;

    // explicit indices are reserved first, then auto slots fill the gaps in DOM order; slots
    // beyond the brand's count wrap around (5 → 1, 6 → 2 …) — see nextIndex()
    const used = new Map<DisplayFormat, Set<number>>();
    const take = (f: DisplayFormat) => used.get(f) ?? (used.set(f, new Set()), used.get(f)!);
    for (const r of all) if (r.index !== undefined && inPlay(r)) take(r.format).add(r.index);
    const next = new Map<DisplayFormat, number>();
    const overflow = new Map<DisplayFormat, number>();
    const stale: GptSlot[] = [];
    for (const r of all) {
      if (!inPlay(r)) {
        if (r.slot) {
          stale.push(r.slot);
          r.slot = undefined;
          r.device = undefined;
        }
        continue;
      }
      let index = r.index;
      let wrapped = false;
      if (index === undefined) {
        const max = countFor(brand, r.format);
        const pick = nextIndex(take(r.format), next.get(r.format) ?? 1, max, overflow.get(r.format) ?? 0);
        index = pick.index;
        wrapped = pick.wrapped;
        next.set(r.format, pick.from);
        overflow.set(r.format, pick.overflow);
      }
      if (wrapped) r.el.setAttribute("data-ad-overflow", ""); else r.el.removeAttribute("data-ad-overflow");
      const code = unitCode(brand, r.format, index);
      // legacyPath (an existing client unit) is honoured in pilot mode only; otherwise the taxonomy path
      const path = GAM_PILOT && r.legacyPath ? r.legacyPath : unitPath(brand, code);
      if (r.code !== code) {
        // A slot that mounts later and sits earlier in the DOM renumbers the ones
        // already defined (the monthly feed's second LREC pushes the rail's last
        // rectangle from 3 to 4). The old GPT slot keeps the previous path, so
        // destroy it and let the define loop below request the new unit.
        if (r.slot) {
          stale.push(r.slot);
          r.slot = undefined;
          r.device = undefined;
        }
        r.code = code;
        r.path = path;
        r.el.setAttribute("data-ad-slot", code);
        r.el.setAttribute("data-ad-unit", ADS_ENABLED ? path : `/<network>/Web/${brand}/${code}`);
        r.onResolve?.(code, path);
      }
    }
    if (!ADS_ENABLED || !targeting.adsAllowed) return;

    // Pilot mode: only slots pointed at an existing client unit go out.
    const eligible = (r: Registered) => !GAM_PILOT || Boolean(r.legacyPath);
    const toDefine = all.filter((r) => r.slot === undefined && r.sizes[band].length > 0 && rendered(r) && eligible(r));
    for (const r of all) if (r.slot === undefined && (r.sizes[band].length === 0 || !rendered(r) || !eligible(r))) r.slot = null; // not requested on this band
    if (!toDefine.length && !stale.length) return;
    gpt((g) => {
      if (stale.length) g.destroySlots(stale);
      for (const r of toDefine) {
        // `dead`: the registration was torn down while this command sat in the queue (an AdSlot
        // effect re-ran, re-registering the same element) — the newer registration defines it.
        if (!r.el.isConnected || r.slot !== undefined || r.dead) continue;
        // The element may already own a slot from a registration that was replaced before its
        // teardown ran; defining it again is a GPT error ("already associated with another slot").
        const existing = g.pubads().getSlots().find((s) => s.getSlotElementId() === r.id);
        // Same element, same path: the define already happened. A path that no longer
        // matches was destroyed above; if it is still registered, drop it and define the new one.
        if (existing && existing.getAdUnitPath() === r.path) { r.slot = existing; r.device = band; continue; }
        if (existing) g.destroySlots([existing]);
        if (r.outOfPage) {
          r.slot = g.defineOutOfPageSlot(r.path!, r.id);
        } else {
          // A size wider than the slot's own box is never requested: a 620x107 offered by a
          // native-row slot inside a 260px card column would render across the neighbouring
          // column (seen 2026-09-14). Fluid and the 1x1-style tokens always pass; a band left
          // with nothing keeps its list so the request still goes out.
          const boxW = r.el.clientWidth || r.el.parentElement?.clientWidth || 0;
          // 16px of slack so a 728x90 still goes out in a 721px column (the padded home
          // page at 768). fitToBox scales that creative into the box. A 620 in a 260px card does not pass.
          const fitsBox = (s: SizeToken) => { const w = parseInt(String(s), 10); return !boxW || !Number.isFinite(w) || w <= 20 || w <= boxW + 16; };
          const fit = (list: SizeToken[]) => { if (r.overlay) return list; const f = list.filter(fitsBox); return f.length ? f : list; };
          const bands = { desktop: fit(r.sizes.desktop), tablet: fit(r.sizes.tablet), mobile: fit(r.sizes.mobile) };
          const allSizes = [...new Set([...bands.desktop, ...bands.tablet, ...bands.mobile])].map(toGptSize);
          const slot = g.defineSlot(r.path!, allSizes, r.id);
          if (slot) {
            const mapping = g
              .sizeMapping()
              .addSize([1280, 0], bands.desktop.map(toGptSize))
              .addSize([768, 0], bands.tablet.map(toGptSize))
              .addSize([0, 0], bands.mobile.map(toGptSize))
              .build();
            if (mapping) slot.defineSizeMapping(mapping);
            if (r.grow) slot.setSafeFrameConfig({ allowPushExpansion: true, allowOverlayExpansion: true });
          }
          r.slot = slot;
        }
        r.slot?.addService(g.pubads());
        r.device = band;
      }
      for (const r of toDefine) if (r.slot) g.display(r.id);
    });
  }, [brand, targeting.adsAllowed]);

  const schedule = React.useCallback(() => {
    if (syncScheduled.current) return;
    syncScheduled.current = true;
    if (typeof window !== "undefined") window.requestAnimationFrame(sync);
  }, [sync]);

  const register = React.useCallback(
    (reg: SlotRegistration) => {
      const id = reg.el.id || `ad-${brand.toLowerCase()}-${reg.format.toLowerCase()}-${++seq}`;
      reg.el.id = id;
      const r: Registered = { ...reg, id };
      regs.current.set(id, r);
      schedule();
      return () => {
        regs.current.delete(id);
        r.dead = true;
        if (r.slot) gpt((g) => g.destroySlots([r.slot as GptSlot]));
      };
    },
    [brand, schedule],
  );

  const setMeta = React.useCallback((m: PageMeta | null) => setMetaState(m), []);

  const value = React.useMemo<AdRuntime>(
    () => ({ enabled: ADS_ENABLED && targeting.adsAllowed, pilot: GAM_PILOT, gptFailed, preview, brand, targeting, device, register, setMeta }),
    [brand, targeting, device, register, setMeta, preview, gptFailed],
  );

  return (
    <Ctx.Provider value={value}>
      {ADS_ENABLED && <Script id="gpt-js" src={GPT_SRC} strategy="afterInteractive" onError={() => setGptFailed(true)} />}
      {children}
    </Ctx.Provider>
  );
}

/** Page-level overrides: render anywhere inside a page (server components can render it). */
export function PageAdMeta(meta: PageMeta) {
  const rt = useAdRuntime();
  const { page, section, keyword, stockcode } = meta;
  const key = JSON.stringify([page, section, keyword, stockcode]);
  React.useEffect(() => {
    rt.setMeta({ page, section, keyword, stockcode });
    return () => rt.setMeta(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return null;
}

/** Sizes helper for slot components. */
export function useSlotSizes(format: DisplayFormat, override?: Partial<SizeSet>): SizeSet {
  const { brand } = useAdRuntime();
  return React.useMemo(() => sizesFor(brand, format, override), [brand, format, override]);
}
